import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Mail } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { apiClient } from "../../lib/apiClient";

import { BrandMark } from "./BrandMark";
import styles from "./AuthLayout.module.css";

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // `sent` keeps the entered email so we can interpolate it into the
  // confirmation copy ("If an account with X exists…"). Cleared when the user
  // clicks "try again with a different address" — re-renders the form.
  const [sent, setSent] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/api/admin/auth/forgot", { email });
    } catch {
      // Anti-enumeration: always show the same success UI regardless of result
      // (matches the mockup notes — backend returns 204 even when the email
      // does not exist; we swallow other errors for the same reason).
    } finally {
      setSubmitting(false);
      setSent(email);
    }
  }

  if (sent !== null) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <BrandMark />
          <div className={styles.hero}>
            <div className={`${styles.heroIcon} ${styles.heroIconOk}`}>
              <Mail size={32} />
            </div>
            <h1 className={styles.title}>Check your inbox</h1>
            <div className={styles.subtitle}>
              If an account with{" "}
              <strong style={{ color: "var(--text-main)" }}>{sent}</strong>{" "}
              exists, we've sent a link to reset the password.
            </div>
            <div className={styles.infoBox}>
              <span className={styles.icon} aria-hidden>
                <Info size={14} />
              </span>
              <div>
                The link expires in 60 minutes. Didn't receive an email? Check
                spam, or{" "}
                <button
                  type="button"
                  className={styles.link}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    font: "inherit",
                  }}
                  onClick={() => {
                    setSent(null);
                    setEmail("");
                  }}
                >
                  try again
                </button>{" "}
                with a different address.
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            <Link to="/login" className={styles.link}>
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <BrandMark />
        <h1 className={styles.title}>Reset your password</h1>
        <div className={styles.subtitle}>
          Enter your email and we'll send you a link to choose a new password.
        </div>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <Input
            type="email"
            label="Email address"
            placeholder="you@studio.com"
            autoComplete="username"
            required
            value={email}
            disabled={submitting}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            type="submit"
            size="lg"
            className={styles.submit}
            disabled={submitting}
            loading={submitting}
          >
            {submitting ? "Sending…" : "Send reset link"}
          </Button>
        </form>

        <div className={styles.footer}>
          Remembered it?{" "}
          <Link
            to="/login"
            className={styles.link}
            style={
              submitting
                ? { opacity: 0.5, pointerEvents: "none" as const }
                : undefined
            }
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
